// The Shot Map's xG story (?live=1, Ben 2026-10-18 — track 1: "the same information as Catalyst,
// but better"). Catalyst shows season totals ("xG 4.2, goals 6"); this shows the season behind
// them: her running non-penalty goals (green) against her running xG (white, dashed), one point
// per match she shot in. Goals above xG = finishing better than expected; below = worse. Click a
// point → the map shows that match only (the same narrowing as a match dragged in from Matches).

//
// A shot hovered or clicked on the map lights up its match's point (Ben, 2026-10-20): the pitch
// tells the story through `noteShotMatch` on its module element; this listens there.

import { useEffect, useState } from "react";
import type { XgStoryPoint } from "./shotmap-view.js";

const SHOT_MATCH_EVENT = "catalyst:shot-match";

/** The Shot Map's pitch → its xG story: the match of the shot hovered (null = none) or clicked. */
export function noteShotMatch(mod: HTMLElement, kind: "hover" | "select", matchId: number | null): void {
  mod.dispatchEvent(new CustomEvent(SHOT_MATCH_EVENT, { detail: { kind, matchId } }));
}

const W = 300;
const H = 64;
const PAD = 6;

export function XgStory({
  mod,
  points,
  picked,
  selectedMatch,
  onPick,
}: {
  mod: HTMLElement;
  points: readonly XgStoryPoint[];
  /** The match the map is narrowed to, if any (its point is ringed). */
  picked: number | null;
  /** The match of the shot clicked on the map, when the story first draws. */
  selectedMatch: number | null;
  onPick: (p: XgStoryPoint) => void;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const [selected, setSelected] = useState<number | null>(selectedMatch);
  useEffect(() => {
    const on = (e: Event) => {
      const { kind, matchId } = (e as CustomEvent<{ kind: "hover" | "select"; matchId: number | null }>).detail;
      if (kind === "hover") setHovered(matchId);
      else setSelected(matchId);
    };
    mod.addEventListener(SHOT_MATCH_EVENT, on);
    return () => mod.removeEventListener(SHOT_MATCH_EVENT, on);
  }, [mod]);
  if (points.length < 2) return null;
  const last = points[points.length - 1]!;
  const top = Math.max(1, ...points.map((p) => Math.max(p.cumGoals, p.cumXg)));
  const x = (i: number) => PAD + (i / (points.length - 1)) * (W - 2 * PAD);
  const y = (v: number) => H - PAD - (v / top) * (H - 2 * PAD);
  const line = (key: "cumGoals" | "cumXg") => points.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p[key]).toFixed(1)}`).join(" ");
  const diff = last.cumGoals - last.cumXg;

  return (
    <div className="xg-story">
      <div className="xg-story-head">
        <span>
          Non-penalty goals <b className="xg-goals">{last.cumGoals}</b> vs xG <b>{last.cumXg.toFixed(2)}</b>
        </span>
        <span className={diff >= 0 ? "xg-up" : "xg-down"}>
          {diff >= 0 ? "+" : "−"}
          {Math.abs(diff).toFixed(2)} {diff >= 0 ? "above" : "below"} expected
        </span>
      </div>
      <svg className="xg-story-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Goals against expected goals through the season">
        <path className="xg-line-xg" d={line("cumXg")} />
        <path className="xg-line-goals" d={line("cumGoals")} />
      </svg>
      {/* The points as HTML over the chart, so they stay round however wide the module is. */}
      <div className="xg-story-points">
        {points.map((p, i) => (
          <button
            type="button"
            key={p.matchId}
            className={`xg-point${p.matchId === picked ? " on" : ""}${p.goals > 0 ? " scored" : ""}${p.matchId === hovered || p.matchId === selected ? " lit" : ""}`}
            style={{ left: `${((x(i) / W) * 100).toFixed(2)}%`, top: `${((y(p.cumGoals) / H) * 100).toFixed(2)}%` }}
            data-tooltip={`${p.label}\n${p.goals} goal${p.goals === 1 ? "" : "s"} from ${p.xg.toFixed(2)} xG\nSeason so far: ${p.cumGoals} vs ${p.cumXg.toFixed(2)} xG\n${p.matchId === picked ? "Click for the whole season" : "Click to show this match's shots"}`}
            onClick={() => onPick(p)}
          />
        ))}
      </div>
    </div>
  );
}
